/**
 * Small shared helpers for the article editor and the article screens.
 */

const API_BASE: string = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '')

/** Public URL for a media item (GET /media/:id/url redirects to the stored file). */
export const mediaUrl = (id: number | string): string => `${API_BASE}/media/${id}/url`

/** Resolve a relative `/media/:id/url` (as returned in `media.url`) against the API origin. */
export const resolveMediaUrl = (url: string): string => {
    if (!url) return url
    if (/^https?:\/\//i.test(url)) return url
    return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`
}

/**
 * The backend stores image src as a relative `/media/:id/url`. Resolve those
 * against the API origin so images render when the admin app is served from a
 * different origin than the API. (The backend regenerates src on save anyway.)
 */
export const resolveHtmlMediaSrc = (html?: string | null): string => {
    if (!html) return ''
    if (!API_BASE) return html
    return html.replace(/src="(\/media\/\d+\/url)"/g, `src="${API_BASE}$1"`)
}

/** Convert HTML to plain text without executing or loading anything (DOMParser is inert). */
export const htmlToPlainText = (html?: string | null): string => {
    if (!html) return ''
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
}

/** Quill reports an empty editor as `<p><br></p>`; treat that (and image-less blank HTML) as empty. */
export const isRichTextEmpty = (html?: string | null): boolean => {
    if (!html) return true
    if (/<img\b/i.test(html)) return false
    return htmlToPlainText(html).length === 0
}

/** Unique occurrence id for one image placement inside an article. */
export const newOccurrenceId = (articleId?: number | string | null): string => {
    const rand = Math.random().toString(36).slice(2, 8)
    return `article-${articleId ?? 'new'}-inline-image-${Date.now().toString(36)}${rand}`
}

export const slugify = (value: string): string =>
    value
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 200)